import {
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  Backdrop,
  Box,
  Portal,
} from '@mui/material'
import { useState, useContext, useRef } from 'react'
import { styled } from '@mui/material/styles'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { useAuth } from 'react-oidc-context'
import CloseIcon from '@mui/icons-material/Close'
import DescriptionIcon from '@mui/icons-material/Description'
import { ItemsInCollectionDocument } from '@kistill/client/data-access'
import { useSnackbar } from 'notistack'
import { useApolloClient, gql } from '@apollo/client'
import { ApiContext } from '../../contexts/ApiContext'
import { toUserFriendlyError, USER_FRIENDLY_NETWORK_ERROR } from '../../utils/toUserFriendlyError'
import { isValidGuid } from '../../utils/guid'
import Draggable from 'react-draggable'

function parseErrorMessage(body: string): string | null {
  if (!body?.trim()) return null
  try {
    const parsed = JSON.parse(body)
    if (typeof parsed === 'string') return parsed
    if (parsed?.message) return parsed.message
    return body
  } catch {
    return body
  }
}

export interface ExcelImportPopupProps {
  open: boolean
  setOpen: (open: boolean | undefined) => void
  itemCollectionNumber: string
  itemCollectionGuid: string
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

export function ExcelImportPopup({
  open,
  setOpen,
  itemCollectionNumber,
  itemCollectionGuid,
}: ExcelImportPopupProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true)

  const nodeRef = useRef(null)
  const { user } = useAuth()
  const apiPath = useContext(ApiContext)
  const { enqueueSnackbar } = useSnackbar()
  const apolloClient = useApolloClient()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      setSelectedFile(null)
      setIsButtonDisabled(true)
      return
    }
    setSelectedFile(file)
    setIsButtonDisabled(false)
    setErrorMessage(null)
  }

  const handleUpload = async () => {
    if (!selectedFile) return
    if (!isValidGuid(itemCollectionGuid)) {
      setErrorMessage('Ógild auðkenni adfangar.')
      setLoading(false)
      return
    }
    setLoading(true)
    setErrorMessage(null)

    const formData = new FormData()
    formData.append('file', selectedFile)

    const url = `${apiPath}/api/ItemCollections/ImportExcel?itemCollectionGuid=${encodeURIComponent(itemCollectionGuid)}`

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
        body: formData,
      })

      const responseBodyText = await response.text()

      if (response.ok) {
        enqueueSnackbar('Innlestur skjals tókst', { variant: 'success' })
        try {
          const importResult = JSON.parse(responseBodyText || '{}')
          await apolloClient.mutate({
            mutation: gql`
              mutation ReindexImportResult($importResultJson: String!) {
                reindexImportResult(importResultJson: $importResultJson)
              }
            `,
            variables: {
              importResultJson: JSON.stringify(importResult),
            },
          })
        } catch (reindexError) {
          enqueueSnackbar(
            'Innlestur tókst en ekki tókst að uppfæra leit. Má bæta við síðar.',
            { variant: 'warning' }
          )
        }
        await apolloClient.refetchQueries({
          include: [ItemsInCollectionDocument],
        })
        setOpen(false)
      } else {
        const message = toUserFriendlyError(parseErrorMessage(responseBodyText))
        setErrorMessage(message ?? USER_FRIENDLY_NETWORK_ERROR)
      }
    } catch {
      setErrorMessage(USER_FRIENDLY_NETWORK_ERROR)
      enqueueSnackbar('Villa kom upp við innlestur.', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleCancelFile = () => {
    setSelectedFile(null)
    setIsButtonDisabled(true)
    setErrorMessage(null)
  }

  return (
    <Box style={{ position: 'relative' }}>
      <Portal>
        <Backdrop open={open} style={{ zIndex: 1300, position: 'fixed' }} />
      </Portal>
      <Draggable nodeRef={nodeRef}>
        <div ref={nodeRef}>
          <Dialog
            fullWidth
            hideBackdrop={true}
            maxWidth='sm'
            open={open}
            PaperProps={{
              sx: { maxWidth: 'none', width: '70%' },
            }}
          >
            <DialogTitle sx={{ pt: 5 }}>
              Innlestur excel skjals fyrir Aðfang {itemCollectionNumber}
            </DialogTitle>
            <DialogContent dividers>
              <DialogContentText
                component='div'
                sx={{ my: 2 }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {!selectedFile ? (
                  <Button
                    component='label'
                    variant='contained'
                    startIcon={<CloudUploadIcon />}
                  >
                    Hlaða upp skjali
                    <VisuallyHiddenInput
                      type='file'
                      onChange={handleChange}
                      accept='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    />
                  </Button>
                ) : (
                  <Grid container justifyContent='center' alignItems='center'>
                    <Grid>
                      <DescriptionIcon fontSize='medium' />
                    </Grid>
                    <Grid
                      item
                      sx={{ mr: 2, ml: 1 }}
                      style={{ fontSize: 'large' }}
                    >
                      {selectedFile.name}
                    </Grid>
                    <Grid item>
                      <Button onClick={() => handleCancelFile()}>
                        <CloseIcon fontSize='medium' />
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </DialogContentText>
              {errorMessage && (
                <Box
                  role='alert'
                  sx={{
                    mt: 2,
                    mb: 2,
                    p: 2,
                    borderRadius: 1,
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? theme.palette.error.light
                        : theme.palette.error.dark,
                    color: (theme) => theme.palette.error.contrastText,
                    textAlign: 'center',
                    whiteSpace: 'pre-line',
                    fontSize: '0.875rem',
                  }}
                >
                  {errorMessage}
                </Box>
              )}
              <DialogActions sx={{ mt: 4, px: 0 }}>
                <Button
                  sx={{ mr: 2 }}
                  variant='contained'
                  onClick={() => setOpen(false)}
                >
                  Til Baka
                </Button>
                <Button
                  variant='contained'
                  color='secondary'
                  onClick={() => handleUpload()}
                  disabled={isButtonDisabled || loading}
                >
                  Skrá
                </Button>
              </DialogActions>
            </DialogContent>
          </Dialog>
        </div>
      </Draggable>
    </Box>
  )
}
